import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import CustomButton from "../../components/Helpers/CustomButton";

export default function ReportBiometricsOccupancy() {
    const { properties } = useSelector((state) => state.properties);
    const [propertyId, setPropertyId] = useState(0);
    const [overOccupancyOnly, setOverOccupancyOnly] = useState(false);
    const [leaseHolderNotInUnit, setLeaseHolderNotInUnit] = useState(false);
    const [includeIdentityCheck, setIncludeIdentityCheck] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [propertyName, setPropertyName] = useState("");
    const [showReport, setShowReport] = useState(false);
    const [totalUnits, setTotalUnits] = useState(0);
    const [overCapacityCount, setOverCapacityCount] = useState(0);
    const [leaseHolderMismatchCount, setLeaseHolderMismatchCount] = useState(0);

    // Sorting state
    const [sortField, setSortField] = useState("unit_name");
    const [sortDirection, setSortDirection] = useState("asc");

    // Expanded rows state for tenant details
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // Search state
    const [searchTerm, setSearchTerm] = useState("");

    // Function to fetch the biometric occupancy data
    async function fetchOccupancyData() {
        if (!propertyId) {
            alert("Please select a property");
            return;
        }

        setLoading(true);
        setShowReport(false);

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/biometrics_occupancy_data`,
                data: {
                    property_id: propertyId,
                    over_occupancy_only: overOccupancyOnly,
                    lease_holder_not_in_unit: leaseHolderNotInUnit,
                    include_identity_check: includeIdentityCheck,
                },
            });

            // Set the property name for display
            const selectedProperty = properties.find((p) => p.value == propertyId);
            setPropertyName(selectedProperty ? selectedProperty.label : "Selected Property");

            // Set the report data
            setReportData(response.data.units || []);
            setTotalUnits(response.data.total_units || 0);
            setOverCapacityCount(response.data.over_capacity_count || 0);
            setLeaseHolderMismatchCount(response.data.lease_holder_mismatch_count || 0);
            setShowReport(true);
        } catch (error) {
            console.error("Error fetching biometric occupancy data:", error);
            alert("Error fetching biometric occupancy data");
        } finally {
            setLoading(false);
        }
    }

    // Function to export data to Excel
    async function exportToExcel() {
        if (!propertyId) {
            alert("Please select a property");
            return;
        }

        setExportLoading(true);

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/biometrics_occupancy_export`,
                responseType: "blob",
                data: {
                    property_id: propertyId,
                    over_occupancy_only: overOccupancyOnly,
                    lease_holder_not_in_unit: leaseHolderNotInUnit,
                    include_identity_check: includeIdentityCheck,
                },
            });

            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", `Biometrics-Occupancy-${propertyName.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Error exporting biometric occupancy data:", error);
            alert("Error exporting biometric occupancy data");
        } finally {
            setExportLoading(false);
        }
    }

    // Sort data function
    const sortData = (field) => {
        // If clicking the same field, toggle direction
        if (field === sortField) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            // New field, set default direction
            setSortField(field);
            setSortDirection("asc");
        }
    };

    // Get sorted and filtered data
    const getSortedData = () => {
        if (!reportData || reportData.length === 0) return [];

        // Filter by search term
        let filtered = reportData;
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = reportData.filter((unit) => {
                // Search in unit name, floor, property
                const matchesUnit = unit.unit_name?.toLowerCase().includes(lowerSearch);
                const matchesFloor = unit.floor_name?.toLowerCase().includes(lowerSearch);
                const matchesProperty = unit.property_name?.toLowerCase().includes(lowerSearch);
                
                // Search in tenant names
                const matchesTenant = unit.tenants?.some((tenant) => 
                    tenant.name?.toLowerCase().includes(lowerSearch) ||
                    tenant.booking_reference?.toLowerCase().includes(lowerSearch)
                );
                
                return matchesUnit || matchesFloor || matchesProperty || matchesTenant;
            });
        }

        // Sort the filtered data
        const sorted = [...filtered].sort((a, b) => {
            let valueA, valueB;

            switch (sortField) {
                case "property_name":
                    valueA = a.property_name;
                    valueB = b.property_name;
                    break;
                case "unit_name":
                    valueA = a.unit_name;
                    valueB = b.unit_name;
                    break;
                case "floor_name":
                    valueA = a.floor_name;
                    valueB = b.floor_name;
                    break;
                case "adult_occupancy":
                    valueA = a.adult_occupancy;
                    valueB = b.adult_occupancy;
                    break;
                case "child_occupancy":
                    valueA = a.child_occupancy;
                    valueB = b.child_occupancy;
                    break;
                case "total_occupancy":
                    valueA = a.total_occupancy;
                    valueB = b.total_occupancy;
                    break;
                default:
                    return 0;
            }

            if (typeof valueA === "string") {
                return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            } else {
                return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
            }
        });
        return sorted;
    };

    // Toggle row expansion
    const toggleRow = (unitId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(unitId)) {
            newExpanded.delete(unitId);
        } else {
            newExpanded.add(unitId);
        }
        setExpandedRows(newExpanded);
    };

    // Sort indicator component
    const SortIndicator = ({ field }) => {
        if (sortField !== field) return null;
        return <span className="ml-1">{sortDirection === "asc" ? "▲" : "▼"}</span>;
    };

    return (
        <>
            <div className="h-full w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
                <h1 className="text-2xl font-bold mb-6">Biometrics Occupancy Report</h1>

                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Generate Occupancy Report</h2>
                    <p className="mb-4 text-gray-600">This report shows unit occupancy based on biometric access data. Units over capacity are highlighted.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Property</label>
                            <select
                                className="mt-1 block w-full border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                onChange={(e) => setPropertyId(e.target.value)}
                                value={propertyId}
                            >
                                <option value="">Choose One</option>
                                <option value="all">All Properties</option>
                                {properties &&
                                    properties.length > 0 &&
                                    properties.map((p) => (
                                        <option key={p.value} value={p.value}>
                                            {p.label}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-3 mt-6">
                            <div className="flex items-center">
                                <input
                                    id="over-occupancy-only"
                                    type="checkbox"
                                    checked={overOccupancyOnly}
                                    onChange={(e) => setOverOccupancyOnly(e.target.checked)}
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                />
                                <label htmlFor="over-occupancy-only" className="ml-2 block text-sm text-gray-700">
                                    Show only units over capacity
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    id="lease-holder-not-in-unit"
                                    type="checkbox"
                                    checked={leaseHolderNotInUnit}
                                    onChange={(e) => setLeaseHolderNotInUnit(e.target.checked)}
                                    className="h-4 w-4 text-orange-600 focus:ring-orange-600 border-gray-300 rounded"
                                />
                                <label htmlFor="lease-holder-not-in-unit" className="ml-2 block text-sm text-gray-700">
                                    Show only units where lease holder is not in unit
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    id="include-identity-check"
                                    type="checkbox"
                                    checked={includeIdentityCheck}
                                    onChange={(e) => setIncludeIdentityCheck(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-600 border-gray-300 rounded"
                                />
                                <label htmlFor="include-identity-check" className="ml-2 block text-sm text-gray-700">
                                    Include identity document checks (may slow down report)
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <CustomButton
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            text="Generate Report"
                            onClick={() => fetchOccupancyData()}
                            loading={loading}
                        />
                        {showReport && (
                            <CustomButton
                                className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-green-600 bg-green-600 hover:bg-green-700 focus:ring-green-600 focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                                text="Download Excel"
                                onClick={() => exportToExcel()}
                                loading={exportLoading}
                            />
                        )}
                    </div>
                </div>

                {/* Report Results Section */}
                {showReport && (
                    <div className="bg-white rounded-lg shadow p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Report Results: {propertyName}</h2>
                            <div className="flex gap-2">
                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">{totalUnits} units</span>
                                {overCapacityCount > 0 && <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">{overCapacityCount} over capacity</span>}
                                {leaseHolderMismatchCount > 0 && <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">{leaseHolderMismatchCount} lease holder mismatch</span>}
                            </div>
                        </div>

                        {/* Search Input */}
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Search by unit, floor, property, tenant name, or booking reference..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {reportData.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-green-600 text-lg">{overOccupancyOnly ? "Great news! No units are over capacity." : "No units found with capacity settings."}</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Details
                                            </th>
                                            {propertyId === "all" && (
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                    onClick={() => sortData("property_name")}
                                                >
                                                    Property <SortIndicator field="property_name" />
                                                </th>
                                            )}
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("floor_name")}
                                            >
                                                Floor <SortIndicator field="floor_name" />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("unit_name")}
                                            >
                                                Unit <SortIndicator field="unit_name" />
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Unit Type
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("adult_occupancy")}
                                            >
                                                Adults <SortIndicator field="adult_occupancy" />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("child_occupancy")}
                                            >
                                                Children <SortIndicator field="child_occupancy" />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("total_occupancy")}
                                            >
                                                Total <SortIndicator field="total_occupancy" />
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {getSortedData().map((unit, index) => (
                                            <React.Fragment key={unit.unit_id}>
                                                <tr className={unit.is_over_capacity ? "bg-red-50" : unit.lease_holder_not_in_unit ? "bg-orange-50" : (index % 2 === 0 ? "bg-white" : "bg-gray-50")}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <button
                                                            onClick={() => toggleRow(unit.unit_id)}
                                                            className="text-blue-600 hover:text-blue-800 focus:outline-none"
                                                        >
                                                            {expandedRows.has(unit.unit_id) ? "▼" : "▶"}
                                                        </button>
                                                    </td>
                                                    {propertyId === "all" && (
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">{unit.property_name}</div>
                                                        </td>
                                                    )}
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{unit.floor_name}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">{unit.unit_name}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{unit.unit_type_name}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className={`text-sm ${unit.is_over_adult ? "text-red-600 font-semibold" : "text-gray-900"}`}>
                                                            {unit.adult_occupancy} / {unit.adult_capacity}
                                                            {unit.is_over_adult && <span className="ml-2 text-xs">OVER</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className={`text-sm ${unit.is_over_child ? "text-red-600 font-semibold" : "text-gray-900"}`}>
                                                            {unit.child_occupancy} / {unit.child_capacity}
                                                            {unit.is_over_child && <span className="ml-2 text-xs">OVER</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className={`text-sm ${unit.is_over_total ? "text-red-600 font-semibold" : "text-gray-900"}`}>
                                                            {unit.total_occupancy} / {unit.total_capacity}
                                                            {unit.is_over_total && <span className="ml-2 text-xs">OVER</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span
                                                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                                unit.is_over_capacity ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                                                            }`}
                                                        >
                                                            {unit.is_over_capacity ? "OVER CAPACITY" : "OK"}
                                                        </span>
                                                    </td>
                                                </tr>
                                                {expandedRows.has(unit.unit_id) && (
                                                    <tr className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                        <td colSpan={propertyId === "all" ? 9 : 8} className="px-6 py-4">
                                                            <div className="text-sm">
                                                                <h4 className="font-semibold mb-3">Tenant Details:</h4>
                                                                {unit.tenants.length === 0 ? (
                                                                    <p className="text-gray-500">No tenants with biometric access</p>
                                                                ) : (
                                                                    <div className="overflow-x-auto">
                                                                        <table className="min-w-full border border-gray-200">
                                                                            <thead className="bg-gray-100">
                                                                                <tr>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Name</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Lease Holder</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Lease Holder Biometrics</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Ever Scanned Face?</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Linked Chat Nature</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Linked Chat Biometrics</th>
                                                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">Last Scanned Face</th>
                                                                                    {includeIdentityCheck && (
                                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">ID Status</th>
                                                                                    )}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {unit.tenants.map((tenant, tIndex) => (
                                                                                    <tr key={tenant.booking_id} className={tIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                                                        <td className="px-3 py-2 border-b">
                                                                                            <a
                                                                                                href={`/booking/url?user_id=${tenant.user_id}&booking_id=${tenant.booking_id}`}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                                                                            >
                                                                                                {tenant.name}
                                                                                            </a>
                                                                                            <div className="text-xs text-gray-500">{tenant.booking_reference}</div>
                                                                                            {tenant.children_count > 0 && (
                                                                                                <div className="text-xs text-gray-600 mt-1">
                                                                                                    + {tenant.children_count} child(ren): {tenant.children.map((child) => child.name).join(", ")}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b text-center">
                                                                                            {tenant.is_lease_holder ? (
                                                                                                <span className="text-green-600 font-semibold">Y</span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">N</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b text-center">
                                                                                            {tenant.is_lease_holder ? (
                                                                                                tenant.has_biometrics ? (
                                                                                                    <span className="text-green-600 font-semibold">Y</span>
                                                                                                ) : (
                                                                                                    <span className="text-red-600 font-semibold">N</span>
                                                                                                )
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b text-center">
                                                                                            {tenant.hik_first_scan ? (
                                                                                                <span className="text-green-600 font-semibold">Y</span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">N</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b">
                                                                                            {tenant.linked_chat_nature ? (
                                                                                                <span className="text-gray-700">{tenant.linked_chat_nature}</span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b text-center">
                                                                                            {tenant.linked_chat_nature ? (
                                                                                                tenant.has_biometrics ? (
                                                                                                    <span className="text-green-600 font-semibold">Y</span>
                                                                                                ) : (
                                                                                                    <span className="text-red-600 font-semibold">N</span>
                                                                                                )
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="px-3 py-2 border-b">
                                                                                            {tenant.hik_last_scan ? (
                                                                                                <span className="text-gray-700 text-xs">
                                                                                                    {new Date(tenant.hik_last_scan).toLocaleDateString()}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        {includeIdentityCheck && (
                                                                                            <td className="px-3 py-2 border-b text-center">
                                                                                                {tenant.has_approved_id !== null && (
                                                                                                    tenant.has_approved_id ? (
                                                                                                        <span className="text-green-600 font-semibold">Y</span>
                                                                                                    ) : (
                                                                                                        <span className="text-red-600 font-semibold">N</span>
                                                                                                    )
                                                                                                )}
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
